import { useEffect, useRef, useState } from "react";
import {
  OmsExportJob,
  OmsFilterOperator,
  OmsRecordFilter,
  OmsReport,
} from "../lusca-search-api/generated-src";
import { columnValueError } from "../components/dashboard/omsReportFormatting";

export type OmsFilterCondition = OmsRecordFilter & { id: number };
export default function useOmsFilterCriteria(
  report: OmsReport,
  job?: OmsExportJob
) {
  const [criteria, setCriteria] = useState<OmsFilterCondition[]>([
    { id: 0, column: "", operator: OmsFilterOperator.Eq, value: "" },
  ]);
  const [operation, setOperation] = useState<"and" | "or">("and");
  const nextId = useRef(1);
  const restoredJob = useRef<string | null>(null);
  useEffect(() => {
    if (job?.mode === "filtered" && restoredJob.current !== job.jobId) {
      restoredJob.current = job.jobId;
      setCriteria(job.filters.map((criterion, id) => ({ ...criterion, id })));
      nextId.current = job.filters.length;
      setOperation(job.operation);
    }
  }, [job]);
  function update(id: number, change: Partial<OmsFilterCondition>) {
    setCriteria((previous) =>
      previous.map((criterion) =>
        criterion.id === id ? { ...criterion, ...change } : criterion
      )
    );
  }
  function add() {
    const id = nextId.current;
    nextId.current += 1;
    setCriteria((previous) => [
      ...previous,
      { id, column: "", operator: OmsFilterOperator.Eq, value: "" },
    ]);
  }
  function remove(index: number) {
    setCriteria((previous) => previous.filter((_item, i) => i !== index));
  }
  function clear() {
    const id = nextId.current;
    nextId.current += 1;
    setCriteria([
      { id, column: "", operator: OmsFilterOperator.Eq, value: "" },
    ]);
    setOperation("and");
  }
  return {
    criteria,
    operation,
    setOperation,
    update,
    add,
    remove,
    clear,
    filters: criteria.map(({ id: _id, ...criterion }) => criterion),
    valid:
      criteria.length > 0 &&
      criteria.every(
        (criterion) =>
          criterion.column &&
          !columnValueError(
            report.columnTypes?.[criterion.column] || "string",
            criterion.operator,
            criterion.value
          )
      ),
  };
}
export type OmsFilterCriteriaState = ReturnType<typeof useOmsFilterCriteria>;
